import { useState } from 'react';

export default function BookingSection() {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company: '',
    investment_focus: '',
    message: '',
    privacy_consent: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [showCallbackForm, setShowCallbackForm] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.privacy_consent) {
      alert('Bitte stimmen Sie der Datenschutzerklärung zu.');
      return;
    }
    
    if (formData.message.length > 500) {
      alert('Die Nachricht darf maximal 500 Zeichen enthalten.');
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('first_name', formData.first_name);
      formDataToSend.append('last_name', formData.last_name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('company', formData.company);
      formDataToSend.append('investment_focus', formData.investment_focus);
      formDataToSend.append('message', formData.message);
      formDataToSend.append('privacy_consent', formData.privacy_consent ? 'Ja' : 'Nein');

      const response = await fetch('https://readdy.ai/api/form/d4is7cr3rief4cfea7eg', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          first_name: '',
          last_name: '',
          email: '',
          phone: '',
          company: '',
          investment_focus: '',
          message: '',
          privacy_consent: false
        });
        // Scroll to success message
        setTimeout(() => {
          document.getElementById('booking-success-message')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show success overlay
  if (submitStatus === 'success') {
    return (
      <section className="py-20 bg-gradient-to-br from-blue-600 to-indigo-700">
        <div className="max-w-6xl mx-auto px-6">
          <div id="booking-success-message" className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-3xl p-12 md:p-16 text-center shadow-2xl border-4 border-green-400 text-white">
            <div className="text-8xl mb-8 animate-bounce">✅</div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Herzlichen Glückwunsch!
            </h2>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 mb-8">
              <p className="text-2xl md:text-3xl font-semibold mb-4">
                🎉 Ihre Bewerbung war erfolgreich!
              </p>
              <p className="text-xl text-green-50 leading-relaxed">
                Sie haben den ersten Schritt zur Mitgliedschaft im Dubai Business Club gemacht.
              </p>
            </div>
            
            <div className="space-y-6 text-left bg-white/5 rounded-2xl p-8 mb-8">
              <h3 className="text-2xl font-bold text-center mb-6">📋 Die nächsten Schritte:</h3>
              <div className="flex items-start space-x-4">
                <div className="bg-yellow-400 text-black rounded-full w-10 h-10 flex items-center justify-center font-bold text-lg flex-shrink-0">1</div>
                <div>
                  <h4 className="font-bold text-lg mb-2">Bestätigungs-E-Mail</h4>
                  <p className="text-green-50">Sie erhalten innerhalb der nächsten Minuten eine Bestätigung an {formData.email || 'Ihre E-Mail-Adresse'}.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="bg-yellow-400 text-black rounded-full w-10 h-10 flex items-center justify-center font-bold text-lg flex-shrink-0">2</div>
                <div>
                  <h4 className="font-bold text-lg mb-2">Persönliches Gespräch</h4>
                  <p className="text-green-50">Unser Team kontaktiert Sie innerhalb von 7 Tagen für ein individuelles Gespräch.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="bg-yellow-400 text-black rounded-full w-10 h-10 flex items-center justify-center font-bold text-lg flex-shrink-0">3</div>
                <div>
                  <h4 className="font-bold text-lg mb-2">Prüfung Ihrer Bewerbung</h4>
                  <p className="text-green-50">Wir prüfen Ihre Unterlagen und sprechen über Ihre Ziele und Erwartungen.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="bg-yellow-400 text-black rounded-full w-10 h-10 flex items-center justify-center font-bold text-lg flex-shrink-0">4</div>
                <div>
                  <h4 className="font-bold text-lg mb-2">Entscheidung & Mitgliedschaft</h4>
                  <p className="text-green-50">Nach positiver Prüfung erhalten Sie Zugang zum Club und allen Leistungen – inklusive der Dubai-Seminare.</p>
                </div>
              </div>
            </div>

            <div className="bg-yellow-400 text-black rounded-2xl p-6 mb-8">
              <p className="text-lg font-bold mb-2">✨ Club-Mitgliedschaft</p>
              <p className="text-sm font-semibold">Zugang zu allen Seminaren, Events und Leistungen des Dubai Business Club</p>
              <p className="text-xs mt-2">Exklusiver und limitierter Zugang</p>
            </div>

            <button
              onClick={() => setSubmitStatus('idle')}
              className="bg-white text-green-600 px-8 py-4 rounded-xl text-lg font-bold hover:bg-green-50 transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer mb-6"
            >
              Weitere Bewerbung einreichen
            </button>

            <div className="mt-8 text-green-100 text-sm">
              <p className="mb-2">Bei Fragen sind wir jederzeit für Sie da:</p>
              <p className="font-bold text-lg">📧 info@dubai-business-club.de</p>
              <p className="font-bold text-lg mt-2">📱 +49 (0) XXX XXXXXXX</p>
            </div>

            <div className="mt-8 pt-8 border-t border-white/20">
              <p className="text-sm text-green-100">
                💡 <strong>Tipp:</strong> Prüfen Sie auch Ihren Spam-Ordner, falls Sie keine E-Mail erhalten haben.
              </p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-gradient-to-br from-blue-600 to-indigo-700">
      <div className="max-w-6xl mx-auto px-6 text-center text-white">
        <div className="mb-12">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Werden Sie Mitglied
          </h2>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed mb-4">
            Das Dubai-Seminar ist automatisch Teil Ihrer Club-Mitgliedschaft. 
            Bewerben Sie sich jetzt für den exklusiven Zugang zu unserem Netzwerk.
          </p>
          <div className="bg-yellow-400/20 backdrop-blur-sm border-2 border-yellow-400 rounded-2xl p-6 max-w-2xl mx-auto">
            <p className="text-yellow-300 font-bold text-lg mb-2">
              ✨ Exklusiver und limitierter Zugang
            </p>
            <p className="text-yellow-200 text-base">
              Das Seminar in Dubai ist im Mitgliedsbeitrag enthalten – ebenso wie alle weiteren Club-Leistungen
            </p>
          </div>
        </div>

        {/* Membership Benefits */}
        <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 md:p-12 mb-12 border border-white/20">
          <h3 className="text-2xl font-bold mb-8">In Ihrer Mitgliedschaft enthalten:</h3>
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div className="text-center">
              <div className="text-4xl mb-4">🎯</div>
              <h3 className="text-xl font-bold mb-2">5-Tage-Seminar</h3>
              <p className="text-blue-100">Dubai Premium-Programm inklusive</p>
            </div>
            <div className="text-center">
              <div className="text-4xl mb-4">🏨</div>
              <h3 className="text-xl font-bold mb-2">Hotel & Verpflegung</h3>
              <p className="text-blue-100">5-Sterne-Unterkunft mit Vollverpflegung</p>
            </div>
            <div className="text-center">
              <div className="text-4xl mb-4">🌐</div>
              <h3 className="text-xl font-bold mb-2">Zugang zum Netzwerk</h3>
              <p className="text-blue-100">Experten, Investoren & Unternehmer</p>
            </div>
          </div>
          
          <div className="text-center bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-2xl p-6 text-black">
            <p className="text-lg font-bold mb-2">🔑 Alles inklusive in Ihrer Club-Mitgliedschaft</p>
            <p className="text-sm">Keine zusätzlichen Kosten für Seminare, Events oder Zugänge</p>
          </div>
        </div>

        {/* Callback Request Section */}
        <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-2xl p-6 mb-8 text-black max-w-2xl mx-auto">
          <div className="flex items-start md:items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
              <i className="ri-phone-line text-3xl"></i>
            </div>
            <div className="flex-1 text-left">
              <h3 className="text-xl font-bold mb-2">Sie haben noch Fragen?</h3>
              <p className="text-sm mb-3">
                Wir rufen Sie gerne zurück und beantworten alle Ihre Fragen zur Mitgliedschaft, zum Seminar oder zum Club persönlich.
              </p>
              <button
                onClick={() => setShowCallbackForm(!showCallbackForm)}
                className="bg-black text-yellow-400 px-6 py-2 rounded-lg font-bold hover:bg-gray-900 transition-all duration-300 whitespace-nowrap cursor-pointer text-sm"
              >
                {showCallbackForm ? '❌ Schließen' : '📞 Rückruf anfordern'}
              </button>
            </div>
          </div>

          {/* Callback Form */}
          {showCallbackForm && (
            <CallbackFormSeminar onClose={() => setShowCallbackForm(false)} />
          )}
        </div>

        {/* Booking Form */}
        <div className="bg-white rounded-3xl p-8 md:p-12 text-gray-900 shadow-2xl max-w-2xl mx-auto">
          <h3 className="text-2xl md:text-3xl font-bold mb-8 text-blue-600">Bewerbung für Club-Mitgliedschaft</h3>
          
          <form 
            onSubmit={handleSubmit}
            className="space-y-6" 
            data-readdy-form 
            id="seminar-booking"
          >
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Vorname *</label>
                <input 
                  type="text" 
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  required 
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ihr Vorname"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Nachname *</label>
                <input 
                  type="text" 
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  required 
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ihr Nachname"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">E-Mail-Adresse *</label>
              <input 
                type="email" 
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required 
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="ihre.email@beispiel.de"
              />
            </div>
            
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Telefonnummer *</label>
              <input 
                type="tel" 
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                required 
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="+49 xxx xxx xxxx"
              />
            </div>
            
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Unternehmen / Position *</label>
              <input 
                type="text" 
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Firmenname und Ihre Position"
              />
            </div>
            
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Investitionsschwerpunkt</label>
              <select 
                name="investment_focus"
                value={formData.investment_focus}
                onChange={handleInputChange}
                className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Bitte wählen...</option>
                <option value="immobilien">Immobilien-Investments</option>
                <option value="firmengründung">Firmengründung</option>
                <option value="steueroptimierung">Steueroptimierung</option>
                <option value="krypto">Krypto-Investments</option>
                <option value="portfolio">Portfolio-Diversifikation</option>
                <option value="sonstiges">Sonstiges</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Warum möchten Sie Mitglied werden? (optional)</label>
              <textarea 
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                rows={4}
                maxLength={500}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="Ihre Motivation und Ziele... (max. 500 Zeichen)"
              ></textarea>
              <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 Zeichen</p>
            </div>
            
            <div className="flex items-start space-x-3">
              <input 
                type="checkbox" 
                name="privacy_consent"
                checked={formData.privacy_consent}
                onChange={handleInputChange}
                required 
                className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <label className="text-sm text-gray-600">
                Ich stimme der Verarbeitung meiner Daten zur Kontaktaufnahme zu und habe die 
                <a href="/datenschutz" className="text-blue-600 hover:underline cursor-pointer"> Datenschutzerklärung</a> gelesen. *
              </label>
            </div>
            
            <button 
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white px-8 py-4 rounded-lg text-lg font-bold transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {isSubmitting ? 'Wird gesendet...' : 'Jetzt für Mitgliedschaft bewerben'}
            </button>

            {submitStatus === 'error' && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
                <strong>Fehler:</strong> Ihre Bewerbung konnte nicht übermittelt werden. Bitte versuchen Sie es erneut.
              </div>
            )}
          </form>
          
          <div className="mt-8 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-700 font-medium text-center">
              🔑 Das Dubai-Seminar ist automatisch in Ihrer Club-Mitgliedschaft enthalten
            </p>
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="mt-12 grid md:grid-cols-3 gap-8 text-center">
          <div>
            <div className="text-3xl mb-3">🔒</div>
            <h4 className="font-bold mb-2">100% Sicher</h4>
            <p className="text-blue-100 text-sm">SSL-verschlüsselte Datenübertragung</p>
          </div>
          <div>
            <div className="text-3xl mb-3">⚡</div>
            <h4 className="font-bold mb-2">Rückmeldung in 7 Tagen</h4>
            <p className="text-blue-100 text-sm">Persönliches Gespräch nach Prüfung</p>
          </div>
          <div>
            <div className="text-3xl mb-3">🎯</div>
            <h4 className="font-bold mb-2">Exklusiver Zugang</h4>
            <p className="text-blue-100 text-sm">Limitierte Mitgliederzahl</p>
          </div>
        </div>
      </div>
    </section>
  );
}

// Callback Form Component for Seminar Page
function CallbackFormSeminar({ onClose }: { onClose: () => void }) {
  const [callbackData, setCallbackData] = useState({
    name: '',
    telefon: '',
    email: '',
    bevorzugte_zeit: '',
    nachricht: '',
    datenschutz: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setCallbackData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!callbackData.datenschutz) {
      alert('Bitte stimmen Sie der Datenschutzerklärung zu.');
      return;
    }

    if (callbackData.nachricht.length > 500) {
      alert('Die Nachricht darf maximal 500 Zeichen enthalten.');
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', callbackData.name);
      formDataToSend.append('telefon', callbackData.telefon);
      formDataToSend.append('email', callbackData.email);
      formDataToSend.append('bevorzugte_zeit', callbackData.bevorzugte_zeit);
      formDataToSend.append('nachricht', callbackData.nachricht);
      formDataToSend.append('datenschutz', callbackData.datenschutz ? 'Ja' : 'Nein');

      const response = await fetch('https://readdy.ai/api/form/d4jv1a6gu0gpot0jagqg', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setCallbackData({
          name: '',
          telefon: '',
          email: '',
          bevorzugte_zeit: '',
          nachricht: '',
          datenschutz: false
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitStatus === 'success') {
    return (
      <div className="mt-6 bg-green-600 text-white rounded-xl p-6 text-center">
        <div className="text-5xl mb-4">✅</div>
        <h4 className="text-2xl font-bold mb-3">Vielen Dank!</h4>
        <p className="mb-4">
          Ihre Rückrufanfrage wurde erfolgreich übermittelt. Wir melden uns innerhalb der nächsten 24 Stunden bei Ihnen.
        </p>
        <button
          onClick={onClose}
          className="bg-white text-green-600 px-6 py-2 rounded-lg font-bold hover:bg-green-50 transition-all duration-300 whitespace-nowrap cursor-pointer"
        >
          Schließen
        </button>
      </div>
    );
  }

  return (
    <div className="mt-6 bg-white rounded-xl p-6">
      <h4 className="text-xl font-bold mb-4 text-black">Rückruf anfordern</h4>
      <form onSubmit={handleSubmit} data-readdy-form id="callback-seminar-form" className="space-y-4">
        <div>
          <label htmlFor="callback-seminar-name" className="block text-sm font-semibold text-gray-700 mb-2">
            Ihr Name *
          </label>
          <input
            type="text"
            id="callback-seminar-name"
            name="name"
            value={callbackData.name}
            onChange={handleInputChange}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            placeholder="Vor- und Nachname"
          />
        </div>

        <div>
          <label htmlFor="callback-seminar-telefon" className="block text-sm font-semibold text-gray-700 mb-2">
            Telefonnummer *
          </label>
          <input
            type="tel"
            id="callback-seminar-telefon"
            name="telefon"
            value={callbackData.telefon}
            onChange={handleInputChange}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            placeholder="+49 123 456789"
          />
        </div>

        <div>
          <label htmlFor="callback-seminar-email" className="block text-sm font-semibold text-gray-700 mb-2">
            E-Mail *
          </label>
          <input
            type="email"
            id="callback-seminar-email"
            name="email"
            value={callbackData.email}
            onChange={handleInputChange}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            placeholder="ihre@email.com"
          />
        </div>

        <div>
          <label htmlFor="callback-seminar-zeit" className="block text-sm font-semibold text-gray-700 mb-2">
            Bevorzugte Rückrufzeit
          </label>
          <select
            id="callback-seminar-zeit"
            name="bevorzugte_zeit"
            value={callbackData.bevorzugte_zeit}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
          >
            <option value="">Bitte wählen...</option>
            <option value="vormittags">Vormittags (9-12 Uhr)</option>
            <option value="mittags">Mittags (12-14 Uhr)</option>
            <option value="nachmittags">Nachmittags (14-17 Uhr)</option>
            <option value="abends">Abends (17-20 Uhr)</option>
            <option value="egal">Egal, jederzeit</option>
          </select>
        </div>

        <div>
          <label htmlFor="callback-seminar-nachricht" className="block text-sm font-semibold text-gray-700 mb-2">
            Ihre Fragen (optional)
          </label>
          <textarea
            id="callback-seminar-nachricht"
            name="nachricht"
            value={callbackData.nachricht}
            onChange={handleInputChange}
            rows={3}
            maxLength={500}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
            placeholder="Was möchten Sie gerne besprechen?"
          ></textarea>
          <p className="text-xs text-gray-500 mt-1">{callbackData.nachricht.length}/500 Zeichen</p>
        </div>

        <div className="flex items-start space-x-3">
          <input 
            type="checkbox" 
            id="callback-seminar-datenschutz"
            name="datenschutz"
            checked={callbackData.datenschutz}
            onChange={handleInputChange}
            required 
            className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <label htmlFor="callback-seminar-datenschutz" className="text-sm text-gray-600">
            Ich habe die <a href="/datenschutz" className="text-blue-600 hover:underline cursor-pointer">Datenschutzerklärung</a> zur Kenntnis genommen. *
          </label>
        </div>

        <div className="flex gap-3">
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-3 px-6 rounded-lg font-bold hover:from-blue-700 hover:to-indigo-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
          >
            {isSubmitting ? 'Wird gesendet...' : '📞 Rückruf anfordern'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-bold hover:bg-gray-300 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            Abbrechen
          </button>
        </div>

        {submitStatus === 'error' && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg text-sm">
            <strong>Fehler:</strong> Ihre Anfrage konnte nicht übermittelt werden. Bitte versuchen Sie es erneut.
          </div>
        )}
      </form>
    </div>
  );
}
